/**
 * useInlineEdit — state machine for inline edit rows.
 *
 * Manages editing toggle, draft value, save/cancel, loading, error,
 * and brief success flash. Used by ReviewSummary's EditableRow.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

type UseInlineEditOptions = {
  value: string
  onSave: (value: string) => Promise<void>
  normalize?: (value: string) => string
  validate?: (value: string) => string | null
}

export function useInlineEdit({ value, onSave, normalize, validate }: UseInlineEditOptions) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [])

  const startEdit = useCallback(() => {
    setDraft(value)
    setError(null)
    setSuccess(false)
    setEditing(true)
  }, [value])

  const cancel = useCallback(() => {
    setEditing(false)
    setError(null)
  }, [])

  const save = useCallback(async () => {
    const normalized = normalize ? normalize(draft) : draft.trim()
    if (normalized === value) { setEditing(false); return }
    const err = validate ? validate(normalized) : null
    if (err) { setError(err); return }

    setSaving(true)
    setError(null)
    try {
      await onSave(normalized)
      setEditing(false)
      setSuccess(true)
      timerRef.current = setTimeout(() => { setSuccess(false) }, 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [draft, value, normalize, validate, onSave])

  return { editing, draft, setDraft, saving, error, success, startEdit, cancel, save }
}
